import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, DataTable, Field, Input, useToast, type Column } from '@w7/ui'
import { cmApi, type ExchangeSearchHit } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { canFetchBetfairMarkets, canUpdateBetfairStartTime } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import { FetchResultPanel, type CmFetchEnvelope } from '@/features/fetch/FetchResultPanel'
import { toFetchEnvelope } from '@/features/fetch/fetchEnvelope'
import { CmPanel } from './CmPanel'
import { CatalogWorkflow } from './catalogGuide'

function formatOpenDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

type BfRow = ExchangeSearchHit & { id: string }

export function BetfairMarketFetchPage() {
  const toast = useToast()
  const me = useCmMe()
  const caller = me.isSuccess ? me.data?.caller : undefined
  const allowFetch = me.isSuccess && canFetchBetfairMarkets(caller)
  const allowStartTime = me.isSuccess && canUpdateBetfairStartTime(caller)
  const [keyword, setKeyword] = useState('')
  const [events, setEvents] = useState<ExchangeSearchHit[]>([])
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [fetchEnvelope, setFetchEnvelope] = useState<CmFetchEnvelope | null>(null)
  const quietSearch = useRef(false)

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, on]) => on).map(([id]) => id),
    [selected],
  )

  const search = useMutation({
    mutationFn: (q: string) => cmApi.exchangeSearch(authHeaders(), q, 50),
    onSuccess: (data) => {
      setEvents(data.events)
      setSelected({})
      if (!quietSearch.current) {
        toast.success(`${data.events.length} event(s) from exchange`)
      }
      quietSearch.current = false
    },
    onError: (err: Error) => {
      quietSearch.current = false
      toast.error(err.message)
    },
  })

  // Lab betfair-market `onchange` — live search while typing.
  useEffect(() => {
    const q = keyword.trim()
    if (q.length < 2) return
    const t = window.setTimeout(() => {
      quietSearch.current = true
      search.mutate(q)
    }, 400)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce keyword only
  }, [keyword])

  const fetchOne = useMutation({
    mutationFn: (eventId: string) => cmApi.exchangeFetchMarkets(authHeaders(), [eventId]),
    onSuccess: (data) => {
      setFetchEnvelope(toFetchEnvelope(data))
      const r = data as { message?: string; processed?: string[] }
      toast.success(r.message || `Fetched ${(r.processed ?? []).length} event(s)`)
    },
    onError: (err: Error) => {
      setFetchEnvelope(null)
      toast.error(err.message)
    },
  })

  const fetchMany = useMutation({
    mutationFn: (eventIds: string[]) => cmApi.exchangeFetchMarkets(authHeaders(), eventIds),
    onSuccess: (data) => {
      setFetchEnvelope(toFetchEnvelope(data))
      const r = data as { message?: string; processed?: string[]; errors?: unknown[] }
      const errCount = Array.isArray(r.errors) ? r.errors.length : 0
      toast.success(
        errCount
          ? `${r.message || 'Fetch done'} · ${errCount} error(s)`
          : r.message || `Fetched ${(r.processed ?? []).length} event(s)`,
      )
    },
    onError: (err: Error) => {
      setFetchEnvelope(null)
      toast.error(err.message)
    },
  })

  const updateStart = useMutation({
    mutationFn: (eventId: string) => cmApi.exchangeUpdateStartTime(authHeaders(), eventId),
    onSuccess: (data, eventId) => {
      const openDate = (data as { openDate?: string }).openDate
      if (openDate) {
        setEvents((prev) =>
          prev.map((e) => (e.eventId === eventId ? { ...e, openDate } : e)),
        )
      }
      toast.success(
        openDate
          ? `Start time updated · ${formatOpenDate(openDate)}`
          : 'Start time updated',
      )
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onSearch = (e: FormEvent) => {
    e.preventDefault()
    const q = keyword.trim()
    if (!q) {
      toast.error('Enter an event name or Betfair id')
      return
    }
    quietSearch.current = false
    search.mutate(q)
  }

  const busy =
    search.isPending || fetchOne.isPending || fetchMany.isPending || updateStart.isPending

  const tableRows = useMemo(
    () => events.map((e) => ({ ...e, id: e.eventId })),
    [events],
  )

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const columns = useMemo((): Column<BfRow>[] => {
    return [
      {
        id: 'n',
        header: '#',
        width: 48,
        numeric: true,
        cell: (_row, i) => i + 1,
      },
      {
        id: 'name',
        header: 'Event',
        cell: (row) => <strong>{row.name}</strong>,
        sortValue: (row) => row.name,
      },
      {
        id: 'openDate',
        header: 'Event date',
        cell: (row) => formatOpenDate(row.openDate),
        sortValue: (row) => row.openDate ?? '',
      },
      {
        id: 'eventId',
        header: 'Betfair id',
        cell: (row) => <code className="cm-mono">{row.eventId}</code>,
        sortValue: (row) => row.eventId,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: (row) => (
          <div className="cm-bf-actions">
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={!allowFetch || busy}
              busy={fetchOne.isPending && fetchOne.variables === row.eventId}
              onClick={() => fetchOne.mutate(row.eventId)}
            >
              Fetch
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={!allowStartTime || busy}
              busy={updateStart.isPending && updateStart.variables === row.eventId}
              onClick={() => updateStart.mutate(row.eventId)}
            >
              Update start time
            </Button>
          </div>
        ),
      },
    ]
  }, [
    allowFetch,
    allowStartTime,
    busy,
    fetchOne.isPending,
    fetchOne.variables,
    updateStart.isPending,
    updateStart.variables,
  ])

  return (
    <CmPanel
      title="Betfair Market Fetch"
      subtitle="Search the exchange, import markets into the portal, and fix start times"
      actions={
        <Button
          type="button"
          variant="secondary"
          disabled={!allowFetch || !selectedIds.length || busy}
          busy={fetchMany.isPending}
          onClick={() => fetchMany.mutate(selectedIds)}
        >
          Fetch selected ({selectedIds.length})
        </Button>
      }
    >
      <CatalogWorkflow
        steps={[
          {
            title: 'Search',
            body: 'Type an event name — results update as you type (min 2 characters).',
          },
          {
            title: 'Fetch',
            body: 'Fetch one row or select many and use Fetch selected. Upserts catalog + rates.',
          },
          {
            title: 'Start time',
            body: 'Update start time refreshes openDate on the event and Match Odds market.',
          },
        ]}
      />

      <form className="cm-msel-search" onSubmit={onSearch}>
        <Field
          label="Search game"
          hint="Event name or Betfair event id. Same search as Backend-UI Betfair Market Fetch."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={keyword}
              onChange={(ev) => setKeyword(ev.target.value)}
              placeholder="Sri Lanka v India or Betfair event id"
            />
          )}
        </Field>
        <Button type="submit" variant="primary" busy={search.isPending}>
          Search
        </Button>
      </form>

      <p className="cm-hint">
        Fetch clears Redis catalog cache and notifies Socket-API so players see new markets immediately.
      </p>

      <DataTable
        label="Exchange events"
        density="compact"
        rows={tableRows}
        columns={columns}
        rowKey={(row) => row.eventId}
        loading={search.isPending}
        maxHeight={420}
        selectable={{
          selected: selectedSet,
          onChange: (next) => {
            const map: Record<string, boolean> = {}
            for (const id of next) map[id] = true
            setSelected(map)
          },
          rowLabel: (row) => ['Choose', row.name].join(' '),
        }}
        empty={
          <p className="cm-hint">
            {search.isPending ? 'Searching exchange…' : 'Search result will be listed here.'}
          </p>
        }
      />

      <FetchResultPanel
        data={fetchEnvelope}
        emptyLabel="Fetch result appears here after fetching markets for one or more events."
        aria-label="betfair-fetch-result"
      />
    </CmPanel>
  )
}
